import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeCanvas } from 'qrcode.react';
import { useTranslation } from 'react-i18next';

// Das neue, stylische Logout-Modal
const LogoutModal = ({ accessKey, onConfirm, onCancel }) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const magicLink = accessKey ? `${window.location.origin}/start?key=${accessKey}` : '';

    const handleCopy = () => {
        if (!magicLink) return;
        navigator.clipboard.writeText(magicLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="modal-backdrop" onClick={onCancel}>
            <motion.div 
                className="modal-content"
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.7, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 150, damping: 20 }}
                style={{ textAlign: 'center' }}
                onClick={e => e.stopPropagation()}
            >
                <h2 style={{ fontFamily: 'var(--font-display)', color: 'var(--color-text-primary)', marginTop: 0 }}>Save Your Access Key!</h2>
                <p style={{ color: 'var(--color-text-secondary)' }}>You'll need this key to log back in. Save it now!</p>

                {magicLink && (
                    <div onClick={handleCopy} style={{ cursor: 'pointer', margin: '25px 0' }}>
                        <QRCodeCanvas value={magicLink} size={160} bgColor="var(--color-surface)" fgColor="var(--color-accent)" style={{ borderRadius: '16px', border: '1px solid var(--color-accent)', padding: '10px' }} />
                        <input type="text" readOnly value={magicLink} style={{ textAlign: 'center', cursor: 'pointer', fontSize: '0.9em', width: '100%', boxSizing: 'border-box', padding: '12px', marginTop: '15px', border: '1px solid var(--color-accent-dim)', borderRadius: '16px', background: 'var(--color-surface)', color: 'var(--color-text-primary)' }} />
                        {copied && <p style={{color: 'var(--color-success)', fontWeight: 'bold', marginTop: '10px'}}>{t('copied_feedback')}</p>}
                    </div>
                )}

                <div style={{ display: 'flex', gap: '15px', marginTop: '20px' }}>
                    <button onClick={onCancel} className="button-primary">Cancel</button>
                    <button onClick={onConfirm} className="button-danger">Log Out</button>
                </div>
            </motion.div>
        </div>
    );
};

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [accessKey, setAccessKey] = useState(null);
    const navigate = useNavigate();

    // Stabile API-Instanz mit useMemo
    const api = useMemo(() => axios.create({
        baseURL: process.env.NODE_ENV === 'production' ? process.env.REACT_APP_API_URL : undefined,
        withCredentials: true
    }), []);

    useEffect(() => {
        const checkAuthStatus = async () => {
            try {
                const res = await api.get('/api/auth/check');
                if (res.data.isAuthenticated) {
                    setUser(res.data.user);
                    setIsAuthenticated(true);
                }
            } catch (error) { /* Silent fail */ }
            finally { setIsLoading(false); }
        };
        checkAuthStatus();
    }, [api]);

    const startSession = async (formData) => {
        try {
            const res = await api.post('/api/auth/start', formData);
            if (res.data.user) {
                setUser(res.data.user);
                setIsAuthenticated(true);
                navigate('/', { replace: true });
            }
        } catch (error) {
            throw error.response?.data?.message || 'Start failed';
        }
    };

    const openLogoutModal = async () => {
        try {
            const res = await api.get('/api/auth/check');
            setAccessKey(res.data.accessKey);
            setShowLogoutModal(true);
        } catch (error) {
            console.error("Could not fetch key for logout, using simple confirm", error);
            if (window.confirm('Are you sure you want to log out?')) {
                confirmLogout();
            }
        }
    };

    const confirmLogout = async () => {
        try {
            await api.post('/api/auth/logout');
            setUser(null);
            setIsAuthenticated(false);
            setShowLogoutModal(false);
            navigate('/start', { replace: true });
        } catch (error) { console.error('Logout failed:', error); }
    };

    const loginWithKey = async (key) => {
        try {
            await api.post('/api/auth/login-key', { key });
            const res = await api.get('/api/auth/check');
            if (res.data.isAuthenticated) {
                setUser(res.data.user);
                setIsAuthenticated(true);
                navigate('/', { replace: true });
            }
        } catch (e) {
            throw new Error('Invalid or expired key.');
        }
    };

    const updateUser = async (data) => {
        try {
            await api.put('/api/auth/user', data);
            const res = await api.get('/api/auth/check');
            if (res.data.isAuthenticated) {
                setUser(res.data.user);
            }
        } catch (error) {
            throw error.response?.data?.message || 'Update failed.';
        }
    };

    const value = { isAuthenticated, user, isLoading, startSession, logout: openLogoutModal, loginWithKey, updateUser, api };

    return (
        <AuthContext.Provider value={value}>
            {children}
            <AnimatePresence>
                {showLogoutModal && (
                    <LogoutModal 
                        accessKey={accessKey}
                        onConfirm={confirmLogout}
                        onCancel={() => setShowLogoutModal(false)}
                    />
                )}
            </AnimatePresence>
        </AuthContext.Provider>
    );
};
